// Author: Sunil Jain
// Timestamp: 2025-04-30 02:23:45 PM EDT
// File: src/pages/PhysicianProfile.js
// Description: Physician/Staff profile page. Handles view/edit of profile details. Password change separate.
// Change: Added Edit/Save/Cancel mode, useMutation for updates, corrected data population from context, aligned payloads with backend schemas.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Grid, TextField, Button,
  CircularProgress, Alert, Link, Divider, Chip,
  IconButton, InputAdornment, Stack // Added Stack for buttons
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useQueryClient, useMutation } from '@tanstack/react-query'; // Import useMutation

import { useAuth, userQueryKey } from '../contexts/AuthContext'; // Import userQueryKey
import authFetch from '../utils/authFetch';
import logger from '../utils/logger';

// TODO: Fetch available specialties from backend for a selector component

// API Mutation function for updating profile
const updateProfileApi = async (payload) => {
    // This function will receive payloads for both user details and potentially staff profile
    const { userDetailsPayload, staffProfilePayload } = payload;
    let userUpdateResponse = null;
    let staffUpdateResponse = null;
    const errors = [];

    // --- Update basic user details if payload exists ---
    if (userDetailsPayload && Object.keys(userDetailsPayload).length > 0) {
        try {
            logger.debug("Attempting to update user details:", userDetailsPayload);
            userUpdateResponse = await authFetch('/users/me/details', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify(userDetailsPayload)
            });
            logger.debug("User details update successful response:", userUpdateResponse);
        } catch(userErr) {
            logger.error("Error updating user details:", userErr);
            errors.push(userErr.message || 'Failed to update basic details.');
        }
    }

    // --- Update staff profile details if payload exists ---
    if (staffProfilePayload && Object.keys(staffProfilePayload).length > 0) {
         try {
            logger.debug("Attempting to update staff profile:", staffProfilePayload);
            staffUpdateResponse = await authFetch('/users/me/staff-profile', {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify(staffProfilePayload)
            });
            logger.debug("Staff profile update successful response:", staffUpdateResponse);
        } catch(staffErr) {
            logger.error("Error updating staff profile:", staffErr);
            // Capture specific error message if available (like the 422 NPI error)
            const detail = staffErr.data?.detail;
            let specificMessage = 'Failed to update professional details.';
            if (Array.isArray(detail) && detail[0]?.msg) {
                specificMessage = detail[0].msg; // Use validation error message
            } else if (typeof detail === 'string') {
                specificMessage = detail;
            } else if (staffErr.message) {
                specificMessage = staffErr.message;
            }
            errors.push(specificMessage);
        }
    }

    // If any errors occurred, throw a combined error message
    if (errors.length > 0) {
        throw new Error(errors.join(' '));
    }

    // Return the potentially updated user profile from the last successful call
    // Backend returns the full ProfileData on successful PATCH to /details or /staff-profile
    return staffUpdateResponse || userUpdateResponse; // Return the latest profile data
};


function PhysicianProfile() {
  const { user, isLoading: authLoading, refetchUserProfile, logout } = useAuth();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false); // State to control edit mode

  // State for Profile Details form
  const [profileFormData, setProfileFormData] = useState({
    first_name: '', last_name: '', email: '', phone_number: '',
    title: '', npi: '', // Use 'npi' to match backend TeamMemberUpdate schema
    // TODO: Replace display string with actual selection logic
    specialty_ids: [], specialty_names_display: '',
  });
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  // Populate form state from user context data
  const populateForm = useCallback(() => {
    if (user) {
      logger.debug("PhysicianProfile: Populating form with user data", user);
      // Access team member profile correctly
      const teamProfile = user.team_member_profile;
      const currentSpecialtyIds = teamProfile?.specialties?.map(s => s.id) || [];
      const currentSpecialtyNames = teamProfile?.specialties?.map(s => s.name)?.join(', ') || '';

      setProfileFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '', // Email is read-only
        phone_number: user.phone_number || '',
        // Populate staff details only if team_member_profile exists
        title: teamProfile?.title || '',
        npi: teamProfile?.npi || '', // Use 'npi' field name from TeamMember model
        specialty_ids: currentSpecialtyIds,
        specialty_names_display: currentSpecialtyNames,
      });
      setProfileError(''); // Clear errors on successful population
      setProfileSuccess('');
    }
  }, [user]); // Dependency on user object

  // Effect to populate form when user data loads or changes
  useEffect(() => {
    if (!authLoading && user) {
      populateForm();
    } else if (!authLoading && !user) {
      logger.error("PhysicianProfile: Auth loaded but no user object found.");
      setProfileError("Could not load user profile data.");
      // Reset form if user becomes null
      setProfileFormData({ first_name: '', last_name: '', email: '', phone_number: '', title: '', npi: '', specialty_ids: [], specialty_names_display: '' });
    }
  }, [user, authLoading, populateForm]);

  // Handler for input changes
  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileFormData(prevState => ({ ...prevState, [name]: value }));
    if (profileError) setProfileError(''); // Clear error on change
    if (profileSuccess) setProfileSuccess(''); // Clear success on change
  };

  // TODO: Implement handleSpecialtyChange with a proper selector component
  // const handleSpecialtyChange = (newValue) => { ... };

  // Mutation hook for updating profile
  const updateProfileMutation = useMutation({
      mutationFn: updateProfileApi, // Use the combined API call function
      onSuccess: (updatedProfileData) => {
          setProfileSuccess('Profile updated successfully!');
          setIsEditing(false); // Exit edit mode on success
          // Update React Query cache with the new data returned from backend API
          if (updatedProfileData) {
             queryClient.setQueryData(userQueryKey, updatedProfileData);
             logger.info("Profile update successful, query cache updated.");
          } else {
             // If backend didn't return updated data (shouldn't happen on success), refetch
             logger.warn("Profile update success, but no data returned. Refetching profile.");
             queryClient.invalidateQueries({ queryKey: userQueryKey });
          }
      },
      onError: (error) => {
          logger.error("Profile update failed:", error);
          setProfileError(error.message || 'Failed to update profile. Please try again.');
      },
  });

  // Handler for Save button
  const handleProfileSubmit = (event) => {
    event.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    logger.debug("PhysicianProfile: Submitting profile update", profileFormData);

    // --- Prepare Payloads ---
    const userDetailsPayload = {};
    if (profileFormData.first_name !== user.first_name) userDetailsPayload.first_name = profileFormData.first_name;
    if (profileFormData.last_name !== user.last_name) userDetailsPayload.last_name = profileFormData.last_name;
    const currentPhone = user.phone_number || '';
    if (profileFormData.phone_number !== currentPhone) userDetailsPayload.phone_number = profileFormData.phone_number || null;

    const staffProfilePayload = {};
    const teamProfile = user.team_member_profile;
    const isStaff = user.role === 'physician_qhp' || user.role === 'clinical_staff' || user.role === 'tenant_admin';
    let npiIsValid = true; // Assume valid unless proven otherwise

    if (isStaff && teamProfile) {
        const currentTitle = teamProfile.title || '';
        const currentNpi = teamProfile.npi || '';
        if (profileFormData.title !== currentTitle) staffProfilePayload.title = profileFormData.title || null;

        // --- ADDED: NPI Validation ---
        const newNpi = profileFormData.npi || null;
        if (newNpi !== currentNpi) { // Only validate if it changed
            if (newNpi) { // Check if not empty/null
                const npiPattern = /^\d{10}$/; // 10 digits pattern
                if (!npiPattern.test(newNpi)) {
                    setProfileError('NPI Number must be exactly 10 digits.');
                    npiIsValid = false; // Mark NPI as invalid
                    logger.warn(`NPI validation failed client-side: ${newNpi}`);
                } else {
                   staffProfilePayload.npi = newNpi; // Add valid NPI to payload
                }
            } else {
                 staffProfilePayload.npi = null; // Allow clearing NPI
            }
        }
        // --- END ADDED: NPI Validation ---

    // TODO: Handle specialty_ids properly when selector is implemented

    } else if (isStaff && !teamProfile) {
        const errorMessage = "Your user role requires a professional profile, but it appears to be missing. Please contact support to resolve this data inconsistency.";
        logger.error(`Submission aborted: ${errorMessage} User ID: ${user.id}`);
        setProfileError(errorMessage);
        // Stop the submission entirely if the user data is in an inconsistent state.
        return;
    }

    // --- Check if any changes AND NPI is valid before submitting ---
    const hasUserChanges = Object.keys(userDetailsPayload).length > 0;
    const hasStaffChanges = Object.keys(staffProfilePayload).length > 0;

    if (!hasUserChanges && !hasStaffChanges) {
        setProfileSuccess("No changes detected.");
        setIsEditing(false);
        return;
    }

    // --- STOP Submission if NPI is invalid ---
    if (!npiIsValid) {
        return; // Error message is already set
    }
    // --- END STOP ---


    // --- Call Mutation ---
    updateProfileMutation.mutate({ userDetailsPayload, staffProfilePayload });
};

  // Handler for Cancel button
  const handleCancelEdit = () => {
      setIsEditing(false);
      populateForm(); // Reset form data to original user data from context
      setProfileError('');
      setProfileSuccess('');
  };


  // --- Password Change Section (Separate Logic - Placeholder for now) ---
  const [passwordFormData, setPasswordFormData] = useState({ current_password: '', new_password: '', confirm_new_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

   // TODO: Define backend API call function for password change
   // const changePasswordApi = async (payload) => { await authFetch('/users/me/password', { method: 'PATCH', body: JSON.stringify(payload) }); };
   // TODO: Implement useMutation hook for password change
   // const changePasswordMutation = useMutation({ mutationFn: changePasswordApi, ... });

   const handlePasswordChange = (event) => {
       const { name, value } = event.target;
       setPasswordFormData(prevState => ({ ...prevState, [name]: value }));
       if(passwordError) setPasswordError('');
       if(passwordSuccess) setPasswordSuccess('');
   };
   const handleClickShowCurrentPassword = () => setShowCurrentPassword((show) => !show);
   const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);
   const handleClickShowConfirmNewPassword = () => setShowConfirmNewPassword((show) => !show);
   const handleMouseDownPassword = (event) => { event.preventDefault(); };

   const handleChangePasswordSubmit = async (event) => {
       event.preventDefault();
       setPasswordError(''); setPasswordSuccess('');
       if (!passwordFormData.current_password || !passwordFormData.new_password || !passwordFormData.confirm_new_password) { setPasswordError('Please fill in all password fields.'); return; }
       if (passwordFormData.new_password !== passwordFormData.confirm_new_password) { setPasswordError('New passwords do not match.'); return; }
       if (passwordFormData.new_password.length < 8) { setPasswordError('New password must be at least 8 characters long.'); return; }
       logger.debug("PhysicianProfile: Submitting password change...");
       setPasswordError('Password change functionality requires backend endpoint.'); // Placeholder
       // TODO: Call the changePasswordMutation when implemented
   };


  // --- Render Logic ---
  if (authLoading) {
    return <Container maxWidth="md" sx={{display: 'flex', justifyContent: 'center', mt: 4}}><CircularProgress /></Container>;
  }
   if (!user) {
       return (
           <Container maxWidth="md">
                <Alert severity="error" sx={{mt: 4}}> Failed to load user profile. Please try <Link component="button" onClick={logout} variant="body2">logging out</Link> and back in. </Alert>
           </Container>
       );
   }

  // Determine if user is staff/qhp based on primary role
  const isStaffOrQhp = user.role === 'physician_qhp' || user.role === 'clinical_staff' || user.role === 'tenant_admin';

  return (
    <Container maxWidth="md">
      {/* --- Profile Details Section --- */}
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography component="h1" variant="h4"> My Profile </Typography>
            {!isEditing && (
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setIsEditing(true)} disabled={updateProfileMutation.isLoading} > Edit Profile </Button>
            )}
        </Box>

        {profileError && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{profileError}</Alert>}
        {profileSuccess && <Alert severity="success" sx={{ mb: 2 }}>{profileSuccess}</Alert>}

        <Box component="form" onSubmit={handleProfileSubmit} noValidate sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            {/* Basic Info */}
            <Grid item xs={12}> <Typography variant="subtitle1" sx={{ mt: 1, fontWeight:'bold' }}>Basic Information</Typography> <Divider sx={{mb:1}}/> </Grid>
            <Grid item xs={12} sm={6}> <TextField required fullWidth name="first_name" label="First Name" value={profileFormData.first_name} onChange={handleProfileChange} disabled={!isEditing || updateProfileMutation.isLoading} InputLabelProps={{ shrink: !!profileFormData.first_name }}/> </Grid>
            <Grid item xs={12} sm={6}> <TextField required fullWidth name="last_name" label="Last Name" value={profileFormData.last_name} onChange={handleProfileChange} disabled={!isEditing || updateProfileMutation.isLoading} InputLabelProps={{ shrink: !!profileFormData.last_name }}/> </Grid>
            <Grid item xs={12}> <TextField fullWidth name="email" label="Email Address" value={profileFormData.email} disabled InputLabelProps={{ shrink: true }} /> </Grid> {/* Email always disabled */}
            <Grid item xs={12}> <TextField fullWidth name="phone_number" label="Phone Number" value={profileFormData.phone_number || ''} onChange={handleProfileChange} disabled={!isEditing || updateProfileMutation.isLoading} InputLabelProps={{ shrink: !!profileFormData.phone_number }}/> </Grid>

            {/* Professional Details - Conditionally render if user is QHP or Staff */}
            {isStaffOrQhp && user.team_member_profile && ( <>
                <Grid item xs={12}> <Typography variant="subtitle1" sx={{ mt: 2, fontWeight:'bold' }}>Professional Details</Typography> <Divider sx={{mb:1}}/> </Grid>
                <Grid item xs={12} sm={6}> <TextField fullWidth name="title" label="Title (e.g., MD, RN)" value={profileFormData.title || ''} onChange={handleProfileChange} disabled={!isEditing || updateProfileMutation.isLoading} InputLabelProps={{ shrink: !!profileFormData.title }} /> </Grid>
                <Grid item xs={12} sm={6}> <TextField required fullWidth name="npi" label="NPI Number" value={profileFormData.npi || ''} onChange={handleProfileChange} disabled={!isEditing || updateProfileMutation.isLoading} InputLabelProps={{ shrink: !!profileFormData.npi }} helperText="10-digit individual NPI" inputProps={{ maxLength: 10 }} /> </Grid>
                <Grid item xs={12}>
                    {/* TODO: Replace TextField with a proper Specialty Selector component */}
                    <TextField fullWidth name="specialty_names_display" label="Specialties" value={profileFormData.specialty_names_display || 'N/A'} disabled InputLabelProps={{ shrink: true }} helperText="Specialty editing requires component implementation." />
                     {/* Display chips in view mode */}
                     {!isEditing && user.team_member_profile?.specialties && user.team_member_profile.specialties.length > 0 && (
                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                             {user.team_member_profile.specialties.map((spec) => ( <Chip key={spec.id} label={spec.name} size="small" /> ))}
                         </Box>
                     )}
                </Grid>
            </> )}
          </Grid>
          {/* Action Buttons for Edit Mode */}
          {isEditing && (
             <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-start' }}>
                 <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={updateProfileMutation.isLoading || authLoading} sx={{ position: 'relative' }} >
                    Save Changes
                    {updateProfileMutation.isLoading && ( <CircularProgress size={24} sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-12px', marginLeft: '-12px' }} /> )}
                  </Button>
                  <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancelEdit} disabled={updateProfileMutation.isLoading} > Cancel </Button>
              </Stack>
          )}
        </Box>
      </Paper>

       {/* --- Change Password Section --- */}
       <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 4 }}>
           <Typography component="h2" variant="h6" gutterBottom> Change Password </Typography>
           {passwordError && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{passwordError}</Alert>}
           {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}
           <Box component="form" onSubmit={handleChangePasswordSubmit} noValidate sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                  <Grid item xs={12}> <TextField required fullWidth margin="dense" name="current_password" label="Current Password" type={showCurrentPassword ? 'text' : 'password'} id="current_password" autoComplete="current-password" value={passwordFormData.current_password} onChange={handlePasswordChange} disabled={updateProfileMutation.isLoading /* TODO: Use password mutation loading state */} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle current password visibility" onClick={handleClickShowCurrentPassword} onMouseDown={handleMouseDownPassword} edge="end"> {showCurrentPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} /> </Grid>
                   <Grid item xs={12}> <TextField required fullWidth margin="dense" name="new_password" label="New Password" type={showNewPassword ? 'text' : 'password'} id="new_password" autoComplete="new-password" value={passwordFormData.new_password} onChange={handlePasswordChange} disabled={updateProfileMutation.isLoading /* TODO: Use password mutation loading state */} helperText="Minimum 8 characters" InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle new password visibility" onClick={handleClickShowNewPassword} onMouseDown={handleMouseDownPassword} edge="end"> {showNewPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} /> </Grid>
                    <Grid item xs={12}> <TextField required fullWidth margin="dense" name="confirm_new_password" label="Confirm New Password" type={showConfirmNewPassword ? 'text' : 'password'} id="confirm_new_password" autoComplete="new-password" value={passwordFormData.confirm_new_password} onChange={handlePasswordChange} disabled={updateProfileMutation.isLoading /* TODO: Use password mutation loading state */} InputProps={{ endAdornment: ( <InputAdornment position="end"> <IconButton aria-label="toggle confirm new password visibility" onClick={handleClickShowConfirmNewPassword} onMouseDown={handleMouseDownPassword} edge="end"> {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />} </IconButton> </InputAdornment> ), }} /> </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                 <Button type="submit" variant="contained" color="secondary" disabled={updateProfileMutation.isLoading /* TODO: Use password mutation loading state */} sx={{ position: 'relative' }}>
                      Change Password
                      {/* TODO: Use password mutation loading state */}
                      {/* {changePasswordMutation.isLoading && ( <CircularProgress size={24} sx={{ ... }} /> )} */}
                  </Button>
                  <Box sx={{ mt: { xs: 2, sm: 0 } }}>
                       <Link component={RouterLink} to="/forgot-password" variant="body2"> Forgot password? </Link>
                  </Box>
              </Box>
           </Box>
       </Paper>
    </Container>
  );
}

export default PhysicianProfile;